import * as Localization from "expo-localization";
import React, { useEffect, useMemo, useState } from "react";

import { Settings } from "luxon";

type TimeZoneProviderValue = {
  timezone: string;
  setTimezone: (tz: string) => any;
};
const defaultTz = Localization.getCalendars()?.[0]?.timeZone ?? "UTC";
const TimeZoneCtx = React.createContext<TimeZoneProviderValue>({
  timezone: defaultTz,
  setTimezone: (tz: string) => {},
});

export const TimeZoneProvider = (
  props: React.PropsWithChildren<{
    timezone?: string;
  }>
) => {
  const parrentTz = useTimeZone();
  const providedTz = props.timezone ?? parrentTz ?? defaultTz;

  const [forzedTz, setForzedTz] = useState<null | string>(null);

  const lowercaseTimezone = useMemo(
    () => (forzedTz ?? providedTz).toLowerCase(),
    [forzedTz, providedTz]
  );

  useEffect(() => {
    Settings.defaultZone = providedTz;

    if ("__setDefaultTimeZone" in Intl.DateTimeFormat) {
      (Intl.DateTimeFormat as any).__setDefaultTimeZone(providedTz);
    }
  }, [parrentTz, providedTz]);
  const value = useMemo(
    () => ({ timezone: lowercaseTimezone, setTimezone: setForzedTz }),
    [lowercaseTimezone]
  );
  return (
    <TimeZoneCtx.Provider value={value}>{props.children}</TimeZoneCtx.Provider>
  );
};

export const useTimeZone = () => {
  return React.useContext(TimeZoneCtx).timezone;
};

export const useSetTimeZone = () => {
  return React.useContext(TimeZoneCtx).setTimezone;
};

export function useForceTimeZone(tz: string | null) {
  const setTimezone = useSetTimeZone();
  useEffect(() => {
    if (tz) {
      setTimezone(tz);
    }
  }, [setTimezone, tz]);
}
